import React from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
const SCROLL_STORAGE_KEY = 'casalabia-scroll-restoration:v1';
const MAX_RESTORE_ATTEMPTS = 10;
function getFallbackKey(pathname, search) {
    return `${pathname}${search}`;
}
function getLocationKeys(location) {
    const fallback = getFallbackKey(location.pathname, location.search);
    return {
        primary: location.key || fallback,
        fallback,
    };
}
function getCurrentScrollY() {
    if (typeof window === 'undefined') {
        return 0;
    }
    return Math.max(0, window.scrollY);
}
function loadPositionsFromSessionStorage() {
    if (typeof window === 'undefined') {
        return new Map();
    }
    try {
        const raw = window.sessionStorage.getItem(SCROLL_STORAGE_KEY);
        if (!raw) {
            return new Map();
        }
        const parsed = JSON.parse(raw);
        if (parsed == null || typeof parsed !== 'object') {
            return new Map();
        }
        const map = new Map();
        Object.entries(parsed).forEach(([key, value]) => {
            if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
                map.set(key, value);
            }
        });
        return map;
    }
    catch {
        return new Map();
    }
}
function persistPositionsToSessionStorage(positions) {
    if (typeof window === 'undefined') {
        return;
    }
    try {
        const payload = Object.fromEntries(positions.entries());
        window.sessionStorage.setItem(SCROLL_STORAGE_KEY, JSON.stringify(payload));
    }
    catch {
        // noop
    }
}
function runWithInstantScrollBehavior(action) {
    const html = document.documentElement;
    const body = document.body;
    const previousHtmlBehavior = html.style.scrollBehavior;
    const previousBodyBehavior = body?.style.scrollBehavior ?? '';
    html.style.scrollBehavior = 'auto';
    if (body) {
        body.style.scrollBehavior = 'auto';
    }
    action();
    window.requestAnimationFrame(() => {
        html.style.scrollBehavior = previousHtmlBehavior;
        if (body) {
            body.style.scrollBehavior = previousBodyBehavior;
        }
    });
}
function scrollToTop() {
    runWithInstantScrollBehavior(() => {
        window.scrollTo({ top: 0, behavior: 'auto' });
    });
}
function restoreScrollWithRetry(targetY, attemptsLeft = MAX_RESTORE_ATTEMPTS) {
    const desiredY = Math.max(0, targetY);
    const maxScrollableY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
    if (maxScrollableY >= desiredY || attemptsLeft <= 1) {
        runWithInstantScrollBehavior(() => {
            window.scrollTo({ top: Math.min(desiredY, maxScrollableY), behavior: 'auto' });
        });
        return;
    }
    window.requestAnimationFrame(() => {
        restoreScrollWithRetry(desiredY, attemptsLeft - 1);
    });
}
function scrollHashTargetInstant(element) {
    runWithInstantScrollBehavior(() => {
        element.scrollIntoView({ block: 'start', behavior: 'auto' });
    });
}
function findHashTarget(hash) {
    if (!hash || hash === '#') {
        return null;
    }
    const rawId = hash.startsWith('#') ? hash.slice(1) : hash;
    const decodedId = decodeURIComponent(rawId);
    const byId = document.getElementById(decodedId);
    if (byId) {
        return byId;
    }
    try {
        return document.querySelector(hash);
    }
    catch {
        return null;
    }
}
export const ScrollRestorationManager = () => {
    const location = useLocation();
    const navigationType = useNavigationType();
    const positionsRef = React.useRef(loadPositionsFromSessionStorage());
    const currentLocationRef = React.useRef(location);
    const previousLocationRef = React.useRef(location);
    const savePositionForLocation = React.useCallback((targetLocation, y) => {
        const nextY = Math.max(0, y);
        const { primary, fallback } = getLocationKeys(targetLocation);
        positionsRef.current.set(primary, nextY);
        positionsRef.current.set(fallback, nextY);
    }, []);
    React.useEffect(() => {
        const historyState = window.history;
        const previousSetting = historyState.scrollRestoration;
        historyState.scrollRestoration = 'manual';
        const flush = () => {
            persistPositionsToSessionStorage(positionsRef.current);
        };
        window.addEventListener('pagehide', flush);
        return () => {
            flush();
            window.removeEventListener('pagehide', flush);
            historyState.scrollRestoration = previousSetting || 'auto';
        };
    }, []);
    React.useEffect(() => {
        let frame = 0;
        const commitCurrentPosition = () => {
            savePositionForLocation(currentLocationRef.current, getCurrentScrollY());
        };
        const onScroll = () => {
            if (frame !== 0) {
                return;
            }
            frame = window.requestAnimationFrame(() => {
                frame = 0;
                commitCurrentPosition();
            });
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', onScroll);
            if (frame !== 0) {
                window.cancelAnimationFrame(frame);
            }
        };
    }, [savePositionForLocation]);
    React.useEffect(() => {
        const previousLocation = previousLocationRef.current;
        savePositionForLocation(previousLocation, getCurrentScrollY());
        persistPositionsToSessionStorage(positionsRef.current);
        currentLocationRef.current = location;
        const hashTarget = findHashTarget(location.hash);
        if (hashTarget) {
            scrollHashTargetInstant(hashTarget);
            previousLocationRef.current = location;
            return;
        }
        if (navigationType === 'POP') {
            const { primary, fallback } = getLocationKeys(location);
            const savedY = positionsRef.current.get(primary) ?? positionsRef.current.get(fallback);
            if (savedY != null) {
                restoreScrollWithRetry(savedY);
            }
            else {
                scrollToTop();
            }
        }
        else {
            scrollToTop();
        }
        previousLocationRef.current = location;
    }, [location, navigationType, savePositionForLocation]);
    return null;
};
